"use client";

import { useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { captureClientEvent } from "@/lib/analytics/client";
import { useAction, useConvexAuth, useQuery } from "convex/react";
import { api } from "@repo/backend/convex/_generated/api";
import { useCreatorReferralReward } from "@/hooks/useCreatorReferralReward";
import { useAuthModal } from "@/auth/auth-modal-provider";
import { setPendingCheckout } from "@/lib/pendingCheckout";
import { useBoard } from "@/components/BoardProvider";
import { PlanSelector } from "@/components/pricing/PlanSelector";
import { SaleBanner } from "@/components/pricing/SaleBanner";
import {
  PASS_STATS,
  formatPrice,
  getPassBenefits,
  getPassPlans,
  priceAfterAward,
} from "@/components/pricing/planContent";
import { HugeiconsIcon } from "@hugeicons/react";

/**
 * The paywall (PricingModal) as a full-bleed page section: accent value panel
 * beside the plan chooser. Signed-out visitors get the plan stashed and are
 * sent to sign up, then resumed by PendingCheckoutResumer.
 */
export function PricingOfferPanel({ className }: { className?: string }) {
  const board = useBoard();
  const createCheckout = useAction(api.autumn.createCheckout);
  const { isAuthenticated } = useConvexAuth();
  const { openAuthModal } = useAuthModal();
  const {
    loading: creatorRewardLoading,
    shouldApply: shouldApplyCreatorReward,
    rewardId: creatorRewardId,
  } = useCreatorReferralReward();

  const [selected, setSelected] = useState<string>("pass_lifetime");
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);
  const plans = getPassPlans();
  // The reward is forced on server-side at checkout; this only makes the price
  // the holder is about to pay the price they see beforehand.
  const awardPercent = useQuery(api.scholarships.getMyAward, isAuthenticated ? {} : "skip");

  const checkoutRewardId =
    !creatorRewardLoading && shouldApplyCreatorReward && creatorRewardId
      ? creatorRewardId
      : undefined;

  const onChooseProduct = useCallback(
    async (uiPlanId: string) => {
      if (pendingPlan) return;
      const plan = plans.find((p) => p.id === uiPlanId) ?? plans[0];
      captureClientEvent("pricing_subscription_checkout_clicked", {
        productId: plan.checkoutId,
        billingPeriod: plan.id === "pass_weekly" ? "weekly" : "lifetime",
        price: plan.amount,
        source: "pricing_page",
      });

      if (!isAuthenticated) {
        setPendingCheckout({
          planId: plan.checkoutId,
          rewardId: checkoutRewardId,
          returnUrl: typeof window !== "undefined" ? window.location.href : undefined,
        });
        openAuthModal("signup");
        return;
      }

      setPendingPlan(uiPlanId);
      const ret = encodeURIComponent(window.location.href);
      const successUrl = `${window.location.origin}/checkout/success?return=${ret}`;
      try {
        const { paymentUrl } = await createCheckout({
          planId: plan.checkoutId,
          successUrl,
          rewardId: checkoutRewardId,
        });
        window.location.href = paymentUrl || successUrl;
      } catch (e) {
        console.error("Checkout failed", e);
        setPendingPlan(null);
      }
    },
    [pendingPlan, plans, isAuthenticated, checkoutRewardId, openAuthModal, createCheckout],
  );

  const benefits = getPassBenefits(board.shortName);
  const selectedPlan = plans.find((p) => p.id === selected) ?? plans[0];

  return (
    <div className={cn("grid lg:grid-cols-2", className)}>
      <div className="flex flex-col bg-primary px-6 py-12 text-white lg:justify-center lg:px-12 lg:py-16">
        <div className="mx-auto w-full max-w-md">
          <h1 className="text-2xl font-semibold leading-snug text-white lg:text-3xl">
            Unlock{" "}
            <span className="underline decoration-white/50 decoration-4 underline-offset-4">unlimited</span>{" "}
            practice and every worked solution
          </h1>
          <p className="mt-2 text-sm text-white/70">
            Every past paper, the full question bank, and worked solutions, unlocked today.
          </p>

          <ul className="mt-6 space-y-3.5 lg:mt-8 lg:space-y-4">
            {benefits.map((b) => (
              <li key={b.title} className="flex gap-3">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white/10">
                  <HugeiconsIcon icon={b.icon} size={20} strokeWidth={1.8} className="text-white" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-white">{b.title}</span>
                  <span className="block text-xs text-white/65">{b.desc}</span>
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-7 grid grid-cols-3 gap-3 border-t border-white/15 pt-6">
            {PASS_STATS.map((s) => (
              <div key={s.label}>
                <p className="text-2xl font-bold leading-none text-white">{s.value}</p>
                <p className="mt-1.5 text-xs leading-tight text-white/65">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* pt/pb asymmetry biases the vertically centered chooser upward for
          above-the-fold density without changing the section height much. */}
      <div className="flex flex-col bg-background px-6 pt-6 pb-12 lg:justify-center lg:px-12 lg:pb-28">
        <div className="mx-auto w-full max-w-md">
          <SaleBanner plans={plans} className="mb-5" />
          <div>
            <h3 className="text-xl font-semibold">Get full access until your {board.shortName}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Practise unlimited questions, past papers and worked solutions.
            </p>
          </div>

          <PlanSelector
            plans={plans}
            selected={selected}
            onSelect={setSelected}
            awardPercent={awardPercent}
            className="mt-5"
          />

          <Button
            className="mt-5 h-14 w-full rounded-xl text-base font-semibold shadow-sm transition-shadow hover:shadow-md md:h-16 md:text-lg"
            onClick={() => onChooseProduct(selected)}
            loading={pendingPlan === selected}
            disabled={pendingPlan !== null}
          >
            {selectedPlan.id === "pass_lifetime" ? "Get lifetime access for " : "Start weekly access for "}
            {formatPrice(priceAfterAward(selectedPlan.amount, awardPercent))}
          </Button>

          {awardPercent ? (
            <p className="mt-3 text-center text-xs text-muted-foreground">
              Your {awardPercent}% scholarship is on your account and comes off at checkout.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
